/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import TextEditor from 'components/TextEditor';
import { CreatePromotionStepProps } from 'domains/promotion/Promotion.types';
import { AdditionalPromotionComponent } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/AdditionalPromotionComponent';
import { Flex, Input, MarginBox, Text } from 'UI';

export const DescriptionSection = ({ promotion, setPromotion, 'data-cy': dataCy }: CreatePromotionStepProps) => {
  const { t } = useTranslation();
  const placeholder = t('common.input.text.placeholder', 'Type your text here');

  const renderTitle = () => {
    return (
      <Flex direction={'column'}>
        <Text type={'h1_banner_light'}>{t('promotion.create_new.title.promotion_details', 'Promotion details')}</Text>
        <Text type="heading3">{t('backoffice.promotion', 'Promotion title*')}</Text>
        <AdditionalPromotionComponent
          promotionType={promotion.promotionType}
          defaultComponent={() => (
            <Flex maxWidth={450}>
              <Input
                value={promotion.title}
                onChange={(val) => setPromotion({ ...promotion, title: val })}
                placeholder={placeholder}
                bordered
                maxLength={63}
                data-cy={'promotion-title-input'}
              />
            </Flex>
          )}
          additionalComponent={() => (
            <Flex maxWidth={450}>
              <Input
                value={promotion.additionalInformation?.title}
                onChange={(val) =>
                  setPromotion({
                    ...promotion,
                    additionalInformation: { ...promotion.additionalInformation, title: val },
                  })
                }
                placeholder={placeholder}
                bordered
                maxLength={63}
              />
            </Flex>
          )}
          handleAddAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: { ...promotion.additionalInformation, containsAdditionalTitle: true },
            })
          }
          handleRemoveAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: {
                ...promotion.additionalInformation,
                containsAdditionalTitle: false,
                title: undefined,
              },
            })
          }
          showAdditionalComponent={promotion.additionalInformation.containsAdditionalTitle}
        />
      </Flex>
    );
  };

  const renderCatchLine = () => {
    return (
      <Flex direction={'column'}>
        <Text type="heading3">{t('backoffice.promotion.catch_line', 'Catch line*')}</Text>
        <AdditionalPromotionComponent
          promotionType={promotion.promotionType}
          defaultComponent={() => (
            <Flex maxWidth={450}>
              <Input
                value={promotion.catchLine}
                onChange={(val) => setPromotion({ ...promotion, catchLine: val })}
                placeholder={placeholder}
                bordered
                maxLength={108}
                data-cy={'catch-line-input'}
              />
            </Flex>
          )}
          additionalComponent={() => (
            <Flex maxWidth={450}>
              <Input
                value={promotion.additionalInformation?.catchLine}
                onChange={(val) =>
                  setPromotion({
                    ...promotion,
                    additionalInformation: { ...promotion.additionalInformation, catchLine: val },
                  })
                }
                placeholder={placeholder}
                bordered
                maxLength={108}
              />
            </Flex>
          )}
          handleAddAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: { ...promotion.additionalInformation, containsAdditionalCatchLine: true },
            })
          }
          handleRemoveAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: {
                ...promotion.additionalInformation,
                containsAdditionalCatchLine: false,
                catchLine: undefined,
              },
            })
          }
          showAdditionalComponent={promotion.additionalInformation.containsAdditionalCatchLine}
        />
      </Flex>
    );
  };

  const renderDescription = () => {
    return (
      <Flex direction={'column'}>
        <Text type="heading3">{t('backoffice.promotion.description', 'Description')}</Text>
        <AdditionalPromotionComponent
          promotionType={promotion.promotionType}
          defaultComponent={() => (
            <Flex maxWidth={450}>
              <TextEditor
                width={450}
                value={promotion.description}
                onChange={(val) => setPromotion({ ...promotion, description: val })}
                placeholder={placeholder}
                preserveWhitespace
                data-cy={'description-input'}
              />
            </Flex>
          )}
          additionalComponent={() => (
            <Flex maxWidth={450}>
              <TextEditor
                width={450}
                value={promotion.additionalInformation?.description}
                onChange={(val) =>
                  setPromotion({
                    ...promotion,
                    additionalInformation: { ...promotion.additionalInformation, description: val },
                  })
                }
                placeholder={placeholder}
                preserveWhitespace
              />
            </Flex>
          )}
          handleAddAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: { ...promotion.additionalInformation, containsAdditionalDescription: true },
            })
          }
          handleRemoveAdditionalComponent={() =>
            setPromotion({
              ...promotion,
              additionalInformation: {
                ...promotion.additionalInformation,
                containsAdditionalDescription: false,
                description: undefined,
              },
            })
          }
          showAdditionalComponent={promotion.additionalInformation.containsAdditionalDescription}
        />
        <MarginBox mt={15} />
      </Flex>
    );
  };

  const getDescriptionItems = () => {
    switch (promotion.promotionType) {
      case 'PROMOTION_FLASH_QUANTITY_LIMITED':
        return (
          <Flex direction={'column'} gap={20}>
            {renderTitle()}
          </Flex>
        );
      case 'DISCOUNT':
      case 'PROMOTION_FLASH_TIME_LIMITED':
      case 'BANNER':
      default:
        return (
          <>
            {renderTitle()}
            {renderCatchLine()}
            {renderDescription()}
          </>
        );
    }
  };

  return <div data-cy={dataCy}>{getDescriptionItems()}</div>;
};
